import { UserService } from '../../api/services/UserService';
import { IdentityService } from '../../api/services/IdentityService';
import { RoleService } from '../../api/services/RoleService';
import { billingManager } from '../billing/BillingManager';
import { config } from '../../config/config';
import type {
  Routemize_DTOs_UserAccountDto,
  Routemize_DTOs_Identity_IdentityUserUpdateDto,
  Routemize_DTOs_Identity_IdentityRoleDto,
  Routemize_Core_Dtos_PagedResultDto_1,
  Routemize_DTOs_Identity_IdentityUserUpdateRolesDto,
  Volo_Abp_PermissionManagement_GetPermissionListResultDto,
  Volo_Abp_PermissionManagement_UpdatePermissionsDto,
  Routemize_DTOs_UserDto,
  Routemize_DTOs_CreateUserDto,
  Routemize_DTOs_UpdateManyUserDto,
  Routemize_DTOs_UserInviteDto,
  Routemize_DTOs_CreateUserInviteDto,
  Routemize_DTOs_UserAddressDto,
} from '../../api/models';
import { Routemize_Constants_AddressType } from '../../api/models/Routemize_Constants_AddressType';
import { UserRoleConfigManager } from './UserRoleConfig';
import { addressManager } from './AddressManager';
import { invalidateTeamMembers } from './teamMembersCache';
import {
  formatUserAddressDisplay,
  resolveHomeUserAddress,
} from '../../utils/userAddressUtils';
import type { Routemize_DTOs_TeamMemberListDto } from '../../api/models/Routemize_DTOs_TeamMemberListDto';


export interface TeamMember {
  id: string;
  name: string;
  initials: string;
  email?: string;
  phoneNumber?: string;
  isActive?: boolean;
  addresses?: Routemize_DTOs_UserAddressDto[];
  homeAddress?: string;
}

/**
 * Map a team-member list DTO to the UI `TeamMember` shape.
 *
 * Pure function (no fetching) so the cached TeamMembersContext can map the
 * roster without an N+1 address round-trip. When `addresses` is omitted it
 * falls back to the `homeAddress` the list endpoint already returns — which is
 * all most callers need. Callers that need a member's *full* address list
 * (e.g. the timeline's selected member) load it lazily and pass it in.
 */
export function mapTeamMemberListDto(
  member: Routemize_DTOs_TeamMemberListDto,
  addresses: Routemize_DTOs_UserAddressDto[] = []
): TeamMember {
  const name = member.name?.trim() || 'Unknown';
  const initials = name.split(' ').map(p => p[0]).join('').toUpperCase().substring(0, 2);
  const resolvedAddresses = addresses.length > 0
    ? addresses
    : member.homeAddress
      ? [{
          addressType: Routemize_Constants_AddressType.HOME,
          addressLine1: member.homeAddress.addressLine1,
          city: member.homeAddress.city,
          state: member.homeAddress.state,
          zipCode: member.homeAddress.zipCode,
          latitude: member.homeAddress.latitude,
          longitude: member.homeAddress.longitude,
          fullAddress: member.homeAddress.fullAddress,
          isActive: true,
        } as Routemize_DTOs_UserAddressDto]
      : [];

  const homeAddress = resolveHomeUserAddress(resolvedAddresses);

  return {
    id: member.id || '',
    name,
    initials,
    email: member.email || undefined,
    isActive: member.isActive,
    addresses: resolvedAddresses,
    homeAddress: formatUserAddressDisplay(homeAddress),
  };
}

export interface UserDto {
  id: string;
  userName: string;
  name: string;
  surname: string;
  email: string;
  phoneNumber: string;
  isActive: boolean;
  lockoutEnabled: boolean;
  isLockedOut: boolean;
  creationTime: string;
  roles: string[];
  avatar?: string;
  tenantId?: string;
  addresses?: Routemize_DTOs_UserAddressDto[];
}

export interface CreateUserDto {
  userName: string;
  name: string;
  surname: string;
  email: string;
  phoneNumber?: string;
  password: string;
  roleNames: string[];
  isActive?: boolean;
  lockoutEnabled?: boolean;
}

export interface UpdateUserDto {
  userName: string;
  name: string;
  surname: string;
  email: string;
  phoneNumber?: string;
  isActive?: boolean;
  lockoutEnabled?: boolean;
  roleNames?: string[];
  password?: string;
  concurrencyStamp?: string;
}

export interface UserFilter {
  filter?: string;
  roleId?: string;
  isActive?: boolean;
  sorting?: string;
  skipCount?: number;
  maxResultCount?: number;
}

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
}

export interface InviteUserDto {
  email: string;
  name: string;
  surname: string;
  roleNames: string[];
  sendInvitationEmail?: boolean;
}


export interface ChangePasswordDto {
  currentPassword?: string;
  newPassword: string;
}

export interface UserValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Business logic manager for user operations
 * Handles user lifecycle, validation, and business rules
 */
export class UserManager {
  private roleCache: Map<string, string> = new Map();
  private loadingUsersPromise: Promise<UserDto[]> | null = null;

  /**
   * Get all users with business logic enhancements
   */
  async getAllUsers(): Promise<UserDto[]> {
    // If we're already loading, return the existing promise to prevent duplicate calls
    if (this.loadingUsersPromise) {
      return this.loadingUsersPromise;
    }
    
    // Create a new loading promise
    this.loadingUsersPromise = (async () => {
      try {
        // Add timeout directly to the API call
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => {
            this.loadingUsersPromise = null; // Clear cached promise on timeout
            reject(new Error('UserService.getApiAppUserGetAllUsers() timeout after 30 seconds'));
          }, 30000)
        );

        const result = await Promise.race([
          UserService.getApiAppUserGetAllUsers(),
          timeoutPromise
        ]);

        const users = this.mapUserDtoArray(result || []);
        return users;
      } catch (error) {
        console.error('UserService.getApiAppUserGetAllUsers() failed:', error);
        this.loadingUsersPromise = null; // Clear cached promise on error
        throw error; // Re-throw to let caller handle it
      } finally {
        // Clear the loading promise when done (only if not already cleared)
        if (this.loadingUsersPromise) {
          this.loadingUsersPromise = null;
        }
      }
    })();
    
    return this.loadingUsersPromise;
  }

  /**
   * Get all team members for UI display.
   *
   * Uses the homeAddress field already present on the list DTO so no per-member
   * address round-trips are fired. Callers that need the full address list for
   * a specific member (e.g. the selected member on the Bookings page) fetch it
   * on demand via addressManager.getUserAddresses().
   */
  async getTeamMembers(): Promise<TeamMember[]> {
    try {
      const members = await UserService.getApiAppUserGetTeamMembers();
      const activeMembers = (members || []).filter(m => m.isActive);
      return activeMembers.map(member => mapTeamMemberListDto(member));
    } catch (error) {
      console.error('UserManager.getTeamMembers() failed:', error);
      throw error;
    }
  }

  /**
   * Get only team members who can be assigned to appointments.
   *
   * Uses the homeAddress field already present on the list DTO so no per-member
   * address round-trips are fired. Callers that need the full address list for
   * a specific member (e.g. the selected member on the Bookings page) fetch it
   * on demand via addressManager.getUserAddresses().
   */
  async getAssignableTeamMembers(): Promise<TeamMember[]> {
    try {
      const members = await UserService.getApiAppUserGetTeamMembers();
      const assignable = (members || []).filter(m => {
        if (!m.isActive) return false;
        const roleType = UserRoleConfigManager.determineRoleType(m.roleNames || []);
        return UserRoleConfigManager.canBeAssignedToEvents(roleType);
      });
      return assignable.map(member => mapTeamMemberListDto(member));
    } catch (error) {
      console.error('Failed to get assignable team members:', error);
      throw error;
    }
  }

  /**
   * Map team member list DTO to TeamMember, preferring active addresses when available.
   */
  /**
   * Map UserDto to TeamMember format
   */
  private mapUserToTeamMember(user: UserDto): TeamMember {
    const fullName = `${user.name} ${user.surname}`.trim() || user.userName;
    const initials = fullName
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);

    // Prefer active home address, then any active address
    const homeAddress = resolveHomeUserAddress(user.addresses);
    const homeAddressString = formatUserAddressDisplay(homeAddress);

    return {
      id: user.id,
      name: fullName,
      initials,
      email: user.email,
      phoneNumber: user.phoneNumber,
      isActive: user.isActive,
      addresses: user.addresses,
      homeAddress: homeAddressString
    };
  }

  /**
   * Get paginated users with filtering
   */
  async getUsers(filter: UserFilter = {}): Promise<PagedResult<UserDto>> {
    const response: Routemize_Core_Dtos_PagedResultDto_1 = await UserService.getApiAppUserGetUsers(
      filter.filter,
      filter.roleId,
      filter.isActive,
      filter.sorting,
      filter.skipCount,
      filter.maxResultCount
    );

    return {
      items: this.mapUserDtoArray((response.items as Routemize_DTOs_UserDto[]) || []),
      totalCount: response.totalCount || 0
    };
  }

  /**
   * Get a single user by ID
   */
  async getUser(id: string): Promise<UserDto | null> {
    if (!id || id === 'undefined' || id === '[object Object]') {
      return null;
    }
    
    const user = await UserService.getApiIdentityUsers1(id);
    return user ? this.mapIdentityUserDto(user) : null;
  }

  /**
   * Create a new user with validation
   */
  async createUser(dto: CreateUserDto): Promise<UserDto> {
    // Validate business rules
    const validation = this.validateCreateUser(dto);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    const createDto: Routemize_DTOs_CreateUserDto = {
      firstName: dto.name,
      lastName: dto.surname,
      email: dto.email,
      phoneNumber: dto.phoneNumber,
      password: dto.password,
      roleId: undefined,
      isAdmin: false,
    };

    const created = await UserService.postApiAppUserCreate(createDto);

    if (dto.roleNames && dto.roleNames.length > 0 && created.id) {
      await this.updateUserRoles(created.id, dto.roleNames);
    }

    invalidateTeamMembers();
    return this.mapIdentityUserDto(created);
  }

  /**
   * Update an existing user
   */
  async updateUser(id: string, dto: UpdateUserDto): Promise<UserDto> {
    // Validate business rules
    const validation = this.validateUpdateUser(dto);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }


    const updateDto: Routemize_DTOs_Identity_IdentityUserUpdateDto = {
      userName: dto.userName,
      name: dto.name,
      surname: dto.surname,
      email: dto.email,
      phoneNumber: dto.phoneNumber,
      isActive: dto.isActive,
      lockoutEnabled: dto.lockoutEnabled,
      roleNames: dto.roleNames,
      password: dto.password,
      concurrencyStamp: dto.concurrencyStamp,
    };

    const updated = await UserService.putApiIdentityUsers(id, updateDto);
    invalidateTeamMembers();
    return this.mapIdentityUserDto(updated);
  }

  /**
   * Delete a user. Returns unused seats info if on Scale with add-on seats.
   */
  async deleteUser(id: string): Promise<{ hasUnusedAddOnSeats?: boolean; unusedAddOnSeats?: number; monthlySavings?: number }> {
    await UserService.deleteApiAppUserDeleteUser(id);
    invalidateTeamMembers();

    // Check for unused add-on seats after deletion
    try {
      const seatsInfo = await billingManager.getUnusedSeatsInfo();
      return seatsInfo || {};
    } catch {
      return {};
    }
  }

  /**
   * Invite a new user
   */
  async inviteUser(dto: InviteUserDto): Promise<Routemize_DTOs_UserInviteDto> {
    // Validate invitation
    const validation = this.validateInviteUser(dto);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    const inviteDto: Routemize_DTOs_CreateUserInviteDto = {
      email: dto.email,
      firstName: dto.name,
      lastName: dto.surname,
      roleName: dto.roleNames?.[0],
    };

    return await UserService.postApiAppUserCreateInvite(inviteDto);
  }

  async cancelInvite(inviteId: string): Promise<void> {
    await UserService.postApiAppUserCancelInvite(inviteId);
  }

  /**
   * Resend invitation to a user
   */
  async resendInvitation(inviteId: string): Promise<void> {
    await UserService.postApiAppUserResendInvite(inviteId);
  }

  /**
   * Refresh busy slots for a user
   * Triggers recalculation of user's busy time slots from appointments, working hours, and external calendars
   * NOTE: Backend endpoint not yet implemented - this is a placeholder
   */
  async refreshBusySlots(userId: string, daysAhead: number = 30): Promise<void> {
    // TODO: Backend needs to implement POST /api/app/user/refreshBusySlots endpoint
  }

  /**
   * Get available roles
   */
  async getRoles(): Promise<Routemize_DTOs_Identity_IdentityRoleDto[]> {

    const roles = await RoleService.getApiIdentityRoles();
    // The generated client collapses every PagedResultDto<T> into one type, so name the item type here.
    const items = (roles?.items || []) as Routemize_DTOs_Identity_IdentityRoleDto[];

    // Cache role names for quick lookup
    items.forEach(role => {
      if (role.id && role.name) {
        this.roleCache.set(role.id, role.name);
      }
    });

    return items;
  }

  /**
   * Get user's roles
   */
  async getUserRoles(userId: string): Promise<Routemize_DTOs_Identity_IdentityRoleDto[]> {

    const roles = await UserService.getApiIdentityUsersRoles(userId);
    return roles?.items || [];
  }

  /**
   * Update user's roles
   */
  async updateUserRoles(userId: string, roleNames: string[]): Promise<void> {

    const updateDto: Routemize_DTOs_Identity_IdentityUserUpdateRolesDto = {
      roleNames,
    };

    await UserService.putApiIdentityUsersRoles(userId, updateDto);
    invalidateTeamMembers();
  }

  /**
   * Change user password
   */
  async changeUserPassword(
    userId: string, 
    currentPassword: string, 
    newPassword: string
  ): Promise<void> {
    // Validate password requirements
    const validation = this.validatePassword(newPassword);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    // ABP's user update endpoint requires userName and email on every PUT.
    // Fetch the current user so we can include them alongside the new password.
    const currentUser = await UserService.getApiIdentityUsers(userId);
    if (!currentUser) {
      throw new Error('User not found');
    }

    await UserService.putApiIdentityUsers(userId, {
      userName: currentUser.userName!,
      email: currentUser.email!,
      name: currentUser.name,
      surname: currentUser.surname,
      phoneNumber: currentUser.phoneNumber,
      isActive: currentUser.isActive,
      lockoutEnabled: currentUser.lockoutEnabled,
      concurrencyStamp: currentUser.concurrencyStamp,
      password: newPassword,
    });
  }

  /**
   * Toggle user lock status
   */
  async toggleUserLock(userId: string, isLocked: boolean): Promise<void> {
    const currentUser = await UserService.getApiIdentityUsers(userId);
    if (!currentUser) {
      throw new Error('User not found');
    }

    await UserService.putApiIdentityUsers(userId, {
      userName: currentUser.userName!,
      email: currentUser.email!,
      name: currentUser.name,
      surname: currentUser.surname,
      phoneNumber: currentUser.phoneNumber,
      isActive: currentUser.isActive,
      lockoutEnabled: isLocked,
      concurrencyStamp: currentUser.concurrencyStamp,
    });
  }

  /**
   * Get role name by ID from cache or API
   */
  async getRoleNameById(roleId: string): Promise<string | null> {
    // Check cache first
    if (this.roleCache.has(roleId)) {
      return this.roleCache.get(roleId) || null;
    }

    // Load roles if not cached
    await this.getRoles();
    return this.roleCache.get(roleId) || null;
  }

  // Business Logic Validation Methods

  private validateCreateUser(dto: CreateUserDto): UserValidationResult {
    const errors: string[] = [];

    if (!dto.userName || dto.userName.length < 3) {
      errors.push('Username must be at least 3 characters');
    }

    if (!dto.email || !this.isValidEmail(dto.email)) {
      errors.push('Valid email is required');
    }

    if (!dto.password || dto.password.length < 6) {
      errors.push('Password must be at least 6 characters');
    }

    if (!dto.name || !dto.surname) {
      errors.push('Name and surname are required');
    }

    if (!dto.roleNames || dto.roleNames.length === 0) {
      errors.push('At least one role must be assigned');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  private validateUpdateUser(dto: UpdateUserDto): UserValidationResult {
    const errors: string[] = [];

    if (dto.userName && dto.userName.length < 3) {
      errors.push('Username must be at least 3 characters');
    }

    if (dto.email && !this.isValidEmail(dto.email)) {
      errors.push('Invalid email format');
    }

    if (dto.password && dto.password.length < 6) {
      errors.push('Password must be at least 6 characters');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  private validateInviteUser(dto: InviteUserDto): UserValidationResult {
    const errors: string[] = [];

    if (!dto.email || !this.isValidEmail(dto.email)) {
      errors.push('Valid email is required');
    }

    if (!dto.roleNames || dto.roleNames.length === 0) {
      errors.push('At least one role must be assigned');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  private validatePassword(password: string): UserValidationResult {
    const errors: string[] = [];

    if (!password || password.length < 6) {
      errors.push('Password must be at least 6 characters');
    }

    // Business rule: Password must contain at least one number and one letter
    if (!/\d/.test(password) || !/[a-zA-Z]/.test(password)) {
      errors.push('Password must contain at least one number and one letter');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Mapping Methods

  private mapIdentityUserDto(user: Routemize_DTOs_UserAccountDto): UserDto {
    return {
      id: String(user.id || ''),
      userName: user.userName || '',
      name: user.name || '',
      surname: user.surname || '',
      email: user.email || '',
      phoneNumber: user.phoneNumber || '',
      isActive: user.isActive ?? true,
      lockoutEnabled: user.lockoutEnabled ?? false,
      isLockedOut: user.isLockedOut ?? false,
      creationTime: user.creationTime || new Date().toISOString(),
      roles: user.roleNames || [],
      tenantId: user.tenantId,
      addresses: user.addresses
    };
  }

  private mapIdentityUserDtoArray(users: Routemize_DTOs_UserAccountDto[]): UserDto[] {
    return users.map(user => this.mapIdentityUserDto(user));
  }

  private mapUserDto(user: Routemize_DTOs_UserDto): UserDto {
    const roleNames = user.roles?.map(r => r.name).filter((n): n is string => !!n) || [];

    return {
      id: String(user.id || ''),
      userName: user.userName || '',
      name: user.name || '',
      surname: user.surname || '',
      email: user.email || '',
      phoneNumber: user.phoneNumber || '',
      isActive: user.isActive ?? true,
      lockoutEnabled: user.lockoutEnabled ?? false,
      isLockedOut: user.isLockedOut ?? false,
      creationTime: user.creationTime || new Date().toISOString(),
      roles: roleNames,
      avatar: user.avatar,
      tenantId: user.tenantId,
      addresses: user.addresses
    };
  }

  private mapUserDtoArray(users: Routemize_DTOs_UserDto[]): UserDto[] {
    return users.map(user => this.mapUserDto(user));
  }

}

// Export singleton instance
// Singleton instance to ensure shared cache across all components
export const userManager = new UserManager();